import React, { useEffect } from "react";
import { SiteHeader, SiteFooter } from "./SiteChrome";
import { CONTACT, APP_ORIGIN } from "./siteConfig";
import "./Site.css";

// Public setup and reference page for the AI assistant connector (the MCP
// server at /mcp), served at /ai-assistants and /claude. Linked as the
// documentation from the Claude and OpenAI directory listings.

// Always the production URL, even when this page is viewed on localhost: it is
// what readers paste into their assistant.
const MCP_URL = `${APP_ORIGIN}/mcp`;

const TOOLS = [
  ["search_projects", "Find projects by name, customer or workflow, with their current stages and who is working on them."],
  ["get_project", "One project in full: current stages, what each stage is waiting on, every stage assignment with its submissions, and stage notes."],
  ["get_stage_history", "Every past visit of one stage on a project — done, returned, rejected, skipped — with time logged and the stage's note thread."],
  ["list_tasks", "Your My Tasks list, or tasks you created, collaborate on, or that belong to a project. Filter by status or name."],
  ["get_task", "One task in full: description, subtasks, dependencies, comments, activity, time logged and attached files."],
  ["get_task_attachment", "The contents of a file attached to a task you can see — images, text, spreadsheets, PDFs and documents, up to 10 MB."],
  ["get_workflow", "A workflow's stages, roles, review steps and connections. Needs permission to view workflows."],
  ["get_audit_logs", "Who changed what and when in your workspace. Admins only, as in the app."],
];

export default function SiteClaudePage() {
  useEffect(() => {
    document.title = "AI assistants — DecisionFlow";
  }, []);

  /* The app shell locks html/body to a non-scrolling 100% viewport and paints
     its own background. Released here for as long as this page is mounted. */
  useEffect(() => {
    document.documentElement.classList.add("site-html");
    document.body.classList.add("site-body");
    return () => {
      document.documentElement.classList.remove("site-html");
      document.body.classList.remove("site-body");
    };
  }, []);

  return (
    <div className="site">
      <SiteHeader />

      <main id="top">
        <section className="site-section">
          <div className="site-container site-legal">
            <p className="site-eyebrow">Integrations</p>
            <h1>Use DecisionFlow with Claude, ChatGPT and Codex</h1>

            <p>
              The DecisionFlow connector lets your AI assistant read your Project &amp;
              Document Management workspace, so you can ask questions instead
              of clicking through screens: <em>why is this project stuck?</em>,{" "}
              <em>what's pending in my backlog?</em>, <em>who changed this
              workflow last week?</em>
            </p>
            <p>
              Access is <strong>read-only</strong>: the assistant cannot create,
              change or delete anything. It sees exactly what you can see in
              the app, through the same permissions.
            </p>

            <h2>Before you connect</h2>
            <ul>
              <li>A DecisionFlow account on Project &amp; Document Management. The connector is not yet available for Manufacturing &amp; Production Tracking.</li>
              <li>Your own DecisionFlow login. Anyone in the workspace can connect, as themselves.</li>
              <li>A Claude account (claude.ai, the desktop or mobile app, or Claude Code), or a ChatGPT account (ChatGPT or Codex).</li>
            </ul>

            <h2>Connect in Claude</h2>
            <ol>
              <li>In Claude, open <strong>Settings → Connectors</strong> and add DecisionFlow. If it isn't listed, choose <strong>Add custom connector</strong> and enter <code>{MCP_URL}</code>.</li>
              <li>Click <strong>Connect</strong>. A DecisionFlow tab opens; sign in as usual.</li>
              <li>Check the request — it names the app asking and where you'll be sent back — and click <strong>Allow read access</strong>.</li>
              <li>You're returned to Claude, connected. Ask it something about your projects or tasks.</li>
            </ol>

            <h2>Connect in Claude Code</h2>
            <p>Run this in a terminal, then type <code>/mcp</code> in Claude Code and choose <strong>Authenticate</strong>:</p>
            <pre><code>{`claude mcp add --transport http decisionflow ${MCP_URL}`}</code></pre>
            <p>
              For scripts or tools that can't sign in through a browser, you
              can create a token under <strong>AI Access</strong> in your
              profile menu in DecisionFlow, which shows the exact command to run.
            </p>

            <h2 id="chatgpt">Connect in ChatGPT</h2>
            <ol>
              <li>In ChatGPT, find DecisionFlow in the apps directory and choose <strong>Connect</strong>.</li>
              <li>Sign in to DecisionFlow and click <strong>Allow read access</strong>. ChatGPT also receives your email address, to identify your account; the consent screen tells you when an app asks for it.</li>
              <li>You're returned to ChatGPT, connected.</li>
            </ol>

            <h2 id="codex">Connect in Codex</h2>
            <p>Add the server to <code>~/.codex/config.toml</code>:</p>
            <pre><code>{`[mcp_servers.decisionflow]\nurl = "${MCP_URL}"`}</code></pre>
            <p>Then sign in from a terminal:</p>
            <pre><code>codex mcp login decisionflow</code></pre>

            <h2>What the assistant can read</h2>
            <ul>
              {TOOLS.map(([name, text]) => (
                <li key={name}>
                  <code>{name}</code> — {text}
                </li>
              ))}
            </ul>
            <p>Things worth trying:</p>
            <ul>
              <li>"Which projects are in progress, and who is working on each?"</li>
              <li>"Why is project [name] stuck? What is it waiting on?"</li>
              <li>"Show the rework history for the Design stage on that project."</li>
              <li>"What tasks are pending in my backlog?"</li>
              <li>"What changed in our workflows this week?" (admins — the audit trail is admin-only, as in the app)</li>
            </ul>

            <h2>Access and security</h2>
            <ul>
              <li><strong>Your permissions, nothing more.</strong> A project, task or audit entry you can't see in DecisionFlow isn't visible to the assistant either.</li>
              <li><strong>Read-only.</strong> There are no tools that write, and the access granted is limited to reading.</li>
              <li><strong>Revocable at any time.</strong> Your connections are listed under <strong>AI Access</strong> in your profile menu, with when each was last used. Workspace admins see everyone's connections there and can revoke any of them. Revoke stops it immediately. Connections also expire after 90 days.</li>
              <li><strong>Recorded.</strong> Connecting and disconnecting appear in your workspace's Audit Trail.</li>
              <li><strong>Profile contact details stay out.</strong> People appear by name; the email addresses and phone numbers on their user profiles are not shared. Text your team typed — comments, descriptions, custom fields — and files attached to tasks are passed on as they are.</li>
            </ul>
            <p>
              How data moves between DecisionFlow and your assistant is covered in
              our <a href="/privacy#claude-connector">privacy policy</a>.
            </p>

            <h2>Troubleshooting</h2>
            <ul>
              <li><strong>"This sign-in request has expired"</strong> — requests last 10 minutes. Start connecting again from your assistant.</li>
              <li><strong>The assistant says it can't find something you can see</strong> — check you connected with the same DecisionFlow account, then disconnect and reconnect the connector to refresh its tool list.</li>
              <li><strong>It stopped working</strong> — the connection may have been revoked or expired. Reconnect from your assistant.</li>
            </ul>

            <h2>Support</h2>
            <p>
              Email{" "}
              <a href={`mailto:${CONTACT.supportEmail}?subject=DecisionFlow AI connector`}>
                {CONTACT.supportEmail}
              </a>{" "}
              or call <a href={`tel:${CONTACT.phoneHref}`}>{CONTACT.phone}</a>.
            </p>
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}
